import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { solicitudApi } from '../../../../api';
import './MisSolicitudes.css';

// ── Mapas de estados ──────────────────────────────────────────────────────────
const ESTADO_META = {
  BORRADOR:                 { label: 'Solicitud recibida',         color: '#888',    bg: '#f3f3f3' },
  EN_REVISION:              { label: 'En revisión documental',     color: '#2471a3', bg: '#eaf4fb' },
  VERIFICANDO_REQUISITOS:   { label: 'Verificando requisitos',     color: '#7d3c98', bg: '#f5eef8' },
  VERIFICANDO_CONDICIONES:  { label: 'Verificando condiciones',    color: '#7d3c98', bg: '#f5eef8' },
  PENDIENTE_REQUISITOS:     { label: 'Pendiente de documentos',    color: '#d68910', bg: '#fef9e7' },
  REQUISITOS_COMPLETOS:     { label: 'Documentos enviados',        color: '#1e8449', bg: '#eafaf1' },
  PENDIENTE_TASACION:       { label: 'Pendiente de tasación',      color: '#2471a3', bg: '#eaf4fb' },
  TASACION_APROBADA:        { label: 'Tasación aprobada',          color: '#1e8449', bg: '#eafaf1' },
  PENDIENTE_APROBACION:     { label: 'Pendiente de aprobación',    color: '#7d3c98', bg: '#f5eef8' },
  EN_EVALUACION:            { label: 'En evaluación crediticia',   color: '#1a4fa0', bg: '#e8f0fb' },
  APROBADO:                 { label: '¡Aprobado!',                 color: '#1e8449', bg: '#eafaf1' },
  DESEMBOLSADO:             { label: 'Desembolsado – crédito activo', color: '#1a4e8a', bg: '#e8f0fb' },
  RECHAZADO:                { label: 'Rechazado',                  color: '#c0392b', bg: '#fdf2f2' },
  CANCELADO:                { label: 'Cancelado',                  color: '#666',    bg: '#f3f3f3' },
  PAGADO:                   { label: 'Pagado',                     color: '#1e8449', bg: '#eafaf1' },
};

const VERIF_META = {
  PENDIENTE:  { label: 'Pendiente',  icon: 'fa-clock',        color: '#d68910', bg: '#fef9e7' },
  APROBADO:   { label: 'Aprobado',   icon: 'fa-circle-check', color: '#1e8449', bg: '#eafaf1' },
  RECHAZADO:  { label: 'Rechazado',  icon: 'fa-circle-xmark', color: '#c0392b', bg: '#fdf2f2' },
  OBSERVADO:  { label: 'Observado',  icon: 'fa-eye',          color: '#7d3c98', bg: '#f5eef8' },
};

const CUOTA_META = {
  PENDIENTE:     { label: 'Pendiente',     color: '#d68910', bg: '#fef9e7' },
  PAGADO:        { label: 'Pagado',        color: '#1e8449', bg: '#eafaf1' },
  VENCIDO:       { label: 'Vencido',       color: '#c0392b', bg: '#fdf2f2' },
  REFINANCIADO:  { label: 'Refinanciado',  color: '#7d3c98', bg: '#f5eef8' },
};

// ── Helpers ───────────────────────────────────────────────────────────────────
const getEstadoConCuotas = (solicitud) => {
  const credito = solicitud?.credito;
  if (credito) {
    const planCuotas = credito.plan_cuotas || [];
    if (planCuotas.length > 0 && planCuotas.every(c => c.estado === 'PAGADO')) {
      return { estado: 'PAGADO', label: 'Pagado' };
    }
    return { 
      estado: credito.estado_proceso || solicitud.estado_solicitud, 
      label: credito.estado_proceso_label || solicitud.estado_solicitud_label 
    };
  }
  return { 
    estado: solicitud.estado_solicitud, 
    label: solicitud.estado_solicitud_label 
  };
};

const filterRequisitos = (requisitos, detalleTipoCredito) => {
  if (!requisitos) return [];
  const subTipo = (detalleTipoCredito?.nombre || '').toLowerCase();
  
  const isIndependiente = subTipo.includes('independiente');
  const isDependiente = subTipo.includes('dependiente') && !isIndependiente;
  
  return requisitos.filter(req => {
    const desc = (req.descripcion || req.nombre || '').toLowerCase();
    
    if (desc.includes('independiente') && isDependiente) {
      return false;
    }
    
    if (desc.includes('dependiente') && !desc.includes('independiente') && isIndependiente) {
      return false;
    }
    
    return true;
  });
};

const estadoBadge = (estado, labelOverride) => {
  const m = ESTADO_META[estado] || { label: estado, color: '#888', bg: '#f3f3f3' };
  return (
    <span className="sol-estado-badge" style={{ color: m.color, background: m.bg }}>
      {labelOverride || m.label}
    </span>
  );
};

const verificBadge = (v) => {
  const m = VERIF_META[v] || VERIF_META.PENDIENTE;
  return (
    <span className="sol-verif-badge" style={{ color: m.color, background: m.bg }}>
      <i className={`fa-solid ${m.icon}`}></i> {m.label}
    </span>
  );
};

const fmt = (n) => parseFloat(n || 0).toLocaleString('es-PE', { minimumFractionDigits: 2 });
const formatDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString('es-PE', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';
const isImage = (url) => /\.(jpg|jpeg|png|gif|webp)$/i.test(url);

// ── StatusStepper ─────────────────────────────────────────────────────────────
const PASOS = [
  { label: 'Recibida',     icon: 'fa-file-pen' },
  { label: 'Revisión',     icon: 'fa-paperclip' },
  { label: 'Evaluación',   icon: 'fa-magnifying-glass' },
  { label: 'Aprobada',     icon: 'fa-circle-check' },
  { label: 'Desembolsada', icon: 'fa-money-bill-wave' },
];

const pasoIndex = (estado) => {
  if (['DESEMBOLSADO', 'PAGADO'].includes(estado)) return 4;
  if (['APROBADO'].includes(estado)) return 3;
  if (['EN_EVALUACION','PENDIENTE_APROBACION','TASACION_APROBADA',
       'PENDIENTE_TASACION','VERIFICANDO_CONDICIONES','VERIFICANDO_REQUISITOS'].includes(estado)) return 2;
  if (['EN_REVISION','REQUISITOS_COMPLETOS','PENDIENTE_REQUISITOS'].includes(estado)) return 1;
  if (['RECHAZADO','CANCELADO'].includes(estado)) return -1;
  return 0;
};

const StatusStepper = ({ estado }) => {
  const idx = pasoIndex(estado);
  const isNeg = idx === -1;
  return (
    <div className="sol-stepper">
      {PASOS.map((paso, i) => {
        const done   = !isNeg && i < idx;
        const active = !isNeg && i === idx;
        const cls    = done ? 'done' : active ? 'active' : 'pending';
        return (
          <React.Fragment key={i}>
            <div className={`sol-step sol-step--${cls}`}>
              <div className="sol-step__circle">
                <i className={`fa-solid ${done ? 'fa-check' : paso.icon}`}></i>
              </div>
              <span className="sol-step__label">{paso.label}</span>
            </div>
            {i < PASOS.length - 1 && (
              <div className={`sol-step__line sol-step__line--${done ? 'done' : 'pending'}`}></div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── ProgresoMini ──────────────────────────────────────────────────────────────
const ProgresoMini = ({ progreso, credito }) => {
  const planCuotas = credito?.plan_cuotas || [];
  if (planCuotas.length > 0) {
    const totalCuotas = planCuotas.length;
    const pagadas = planCuotas.filter(c => c.estado === 'PAGADO').length;
    const pendientes = totalCuotas - pagadas;
    const pct = Math.round((pagadas / totalCuotas) * 100);

    return (
      <div className="sol-progreso-mini">
        <div className="sol-progreso-mini__item">
          <span>Avance de pagos</span>
          <div className="sol-prog-bar">
            <div className="sol-prog-fill" style={{ width: `${pct}%`, background: pct === 100 ? '#27ae60' : '#1a4fa0' }}></div>
          </div>
          <span className="sol-prog-pct">{pagadas} pagadas / {pendientes} pendientes</span>
        </div>
      </div>
    );
  }

  if (!progreso) return null;
  const req  = progreso.requisitos  || {};
  const cond = progreso.condiciones || {};
  const reqPct  = req.total  > 0 ? Math.round((req.aprobados  / req.total)  * 100) : 0;
  const condPct = cond.total > 0 ? Math.round((cond.aprobadas / cond.total) * 100) : 0;
  return (
    <div className="sol-progreso-mini">
      <div className="sol-progreso-mini__item">
        <span>Requisitos</span>
        <div className="sol-prog-bar">
          <div className="sol-prog-fill" style={{ width: `${reqPct}%`, background: reqPct === 100 ? '#27ae60' : '#1a4fa0' }}></div>
        </div>
        <span className="sol-prog-pct">{req.aprobados ?? 0}/{req.total ?? 0}</span>
      </div>
      {cond.total > 0 && (
        <div className="sol-progreso-mini__item">
          <span>Condiciones</span>
          <div className="sol-prog-bar">
            <div className="sol-prog-fill" style={{ width: `${condPct}%`, background: condPct === 100 ? '#27ae60' : '#7d3c98' }}></div>
          </div>
          <span className="sol-prog-pct">{cond.aprobadas ?? 0}/{cond.total ?? 0}</span>
        </div>
      )}
    </div>
  );
};

// ── RequisitoRow ──────────────────────────────────────────────────────────────
const RequisitoRow = ({ req }) => {
  const [open, setOpen] = useState(false);
  const evidencias = req.evidencia_url || [];
  const verif = req.estado_verificacion;
  return (
    <div className={`sol-req-row${verif === 'APROBADO' ? ' sol-req-row--ok' : verif === 'RECHAZADO' ? ' sol-req-row--rej' : verif === 'OBSERVADO' ? ' sol-req-row--obs' : ''}`}>
      <div className="sol-req-row__main">
        <div className="sol-req-row__info">
          <strong>{req.descripcion}</strong>
          <div className="sol-req-row__badges">
            {req.obligatoriedad && (
              <span className={`sol-obl-badge${req.obligatoriedad === 'OBLIGATORIO' ? ' sol-obl-badge--oblig' : ''}`}>{req.obligatoriedad}</span>
            )}
            <span className={`sol-cumple-badge${req.cumple ? ' sol-cumple-badge--si' : ' sol-cumple-badge--no'}`}>
              {req.cumple ? <><i className="fa-solid fa-check"></i> Cumple</> : <><i className="fa-solid fa-xmark"></i> No cumple</>}
            </span>
            {verificBadge(req.estado_verificacion || 'PENDIENTE')}
            {req.cantidad_evidencias > 0 && (
              <span className="sol-evidencia-count"><i className="fa-solid fa-paperclip"></i> {req.cantidad_evidencias} archivo(s)</span>
            )}
          </div>
          {req.observacion && <p className="sol-req-row__obs">{req.observacion}</p>}
        </div>
        {evidencias.length > 0 && (
          <button className="sol-req-toggle" onClick={() => setOpen(o => !o)}>
            <i className={`fa-solid ${open ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i> {open ? 'Ocultar' : 'Ver'} evidencias
          </button>
        )}
      </div>
      {open && (
        <div className="sol-evidencias">
          {evidencias.map((url, i) => (
            <a key={i} href={url} target="_blank" rel="noreferrer" className="sol-evidencia-item">
              {isImage(url)
                ? <img src={url} alt={`Evidencia ${i + 1}`} />
                : <div className="sol-evidencia-file"><i className="fa-solid fa-file-lines"></i><span>{url.split('/').pop()}</span></div>
              }
            </a>
          ))}
        </div>
      )}
    </div>
  );
};

// ── PlanCuotas ────────────────────────────────────────────────────────────────
const PlanCuotas = ({ credito }) => {
  const [expandido, setExpandido] = useState(false);
  const cuotas = credito.plan_cuotas || [];
  if (!cuotas.length) return null;
  const visible  = expandido ? cuotas : cuotas.slice(0, 4);
  const pagadas  = cuotas.filter(c => c.estado === 'PAGADO').length;
  const vencidas = cuotas.filter(c => c.estado === 'VENCIDO').length;

  return (
    <div className="dash-card">
      <div className="sol-cuotas-header">
        <h3 className="sol-detail__section-title">
          <i className="fa-solid fa-calendar-days"></i> Plan de Cuotas
          <span className="sol-detail__section-count">{credito.total_cuotas ?? cuotas.length}</span>
        </h3>
        <div className="sol-cuotas-stats">
          {pagadas  > 0 && <span className="sol-cuota-stat sol-cuota-stat--ok"><i className="fa-solid fa-check"></i> {pagadas} pagadas</span>}
          {vencidas > 0 && <span className="sol-cuota-stat sol-cuota-stat--rej"><i className="fa-solid fa-triangle-exclamation"></i> {vencidas} vencidas</span>}
          <span className="sol-cuota-stat"><i className="fa-solid fa-clock"></i> {cuotas.length - pagadas - vencidas} pendientes</span>
        </div>
      </div>

      <div className="sol-cuotas-warning">
        <i className="fa-solid fa-circle-info"></i>
        <p>
          <strong>Recuerda:</strong> El monto que figura en la columna <strong>CUOTA</strong> es el valor total a pagar, siempre y cuando realices tu pago en la <strong>FECHA PAGO</strong> indicada o antes. Evita cargos adicionales por atraso pagando a tiempo.
        </p>
      </div>

      <div className="sol-cuotas-table-wrap">
        <table className="sol-cuotas-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Fecha pago</th>
              <th>Cuota</th>
              <th>Capital</th>
              <th>Interés</th>
              <th>Saldo final</th>
              <th>Estado</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((c) => {
              const meta = CUOTA_META[c.estado] || CUOTA_META.PENDIENTE;
              return (
                <tr key={c.numero_cuota} className={`sol-cuota-row sol-cuota-row--${(c.estado || '').toLowerCase()}`}>
                  <td className="sol-cuota-num">{c.numero_cuota}</td>
                  <td>{formatDate(c.fecha_pago)}</td>
                  <td><strong>S/ {fmt(c.monto_cuota)}</strong></td>
                  <td>S/ {fmt(c.amortizacion)}</td>
                  <td>S/ {fmt(c.interes)}</td>
                  <td>S/ {fmt(c.saldo_final)}</td>
                  <td>
                    <span className="sol-cuota-badge" style={{ color: meta.color, background: meta.bg }}>
                      {meta.label}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {cuotas.length > 4 && (
        <button className="sol-cuotas-toggle" onClick={() => setExpandido(e => !e)}>
          <i className={`fa-solid ${expandido ? 'fa-chevron-up' : 'fa-chevron-down'}`}></i>
          {expandido ? 'Ver menos' : `Ver las ${cuotas.length - 4} cuotas restantes`}
        </button>
      )}
    </div>
  );
};

// ── Historial ─────────────────────────────────────────────────────────────────
const Historial = ({ historial }) => {
  if (!historial?.length) return null;
  return (
    <div className="dash-card">
      <h3 className="sol-detail__section-title">
        <i className="fa-solid fa-timeline"></i> Historial de estados
        <span className="sol-detail__section-count">{historial.length}</span>
      </h3>
      <div className="sol-historial">
        {historial.map((h, i) => (
          <div key={i} className="sol-historial-item">
            <div className="sol-historial-item__dot"></div>
            <div className="sol-historial-item__body">
              <div className="sol-historial-item__header">
                <span className="sol-historial-item__estado">{h.estado_nuevo}</span>
                <span className="sol-historial-item__fecha">{formatDate(h.fecha)}</span>
              </div>
              {h.descripcion && <p className="sol-historial-item__desc">{h.descripcion}</p>}
              {h.estado_anterior && (
                <span className="sol-historial-item__prev">Anterior: <em>{h.estado_anterior}</em></span>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── SolicitudCard ─────────────────────────────────────────────────────────────
const SolicitudCard = ({ s, onDetail, loadingDetail }) => {
  const credito       = s.credito;
  const tipoNombre    = s.tipo_credito?.nombre    || s.tipoCredito?.nombre    || '';
  const detNombre     = s.detalle_tipo_credito?.nombre || s.detalleTipoCredito?.nombre || '';
  const display       = getEstadoConCuotas(s);
  const isAprobado    = ['APROBADO','DESEMBOLSADO','PAGADO'].includes(display.estado);
  const isRechazado   = display.estado === 'RECHAZADO';

  return (
    <div className={`sol-card${isAprobado ? ' sol-card--aprobado' : isRechazado ? ' sol-card--rechazado' : ''}`}>
      <div className="sol-card__top">
        <div className="sol-card__left">
          <span className="sol-card__code"><i className="fa-solid fa-file-lines"></i> {s.codigo_solicitud}</span>
          {(tipoNombre || detNombre) && (
            <span className="sol-card__tipo">{tipoNombre}{tipoNombre && detNombre ? ' · ' : ''}{detNombre}</span>
          )}
        </div>
        <div className="sol-card__right">
          {estadoBadge(display.estado, display.label)}
          <span className="sol-card__date">{formatDate(s.fecha_solicitud)}</span>
        </div>
      </div>

      <div className="sol-card__amounts">
        <div><label>Monto</label><strong>S/ {fmt(s.monto_solicitado)}</strong></div>
        <div><label>Plazo</label><strong>{s.plazo_solicitado} meses</strong></div>
        <div><label>Frecuencia</label><strong>{s.frecuencia_pago}</strong></div>
        {s.monto_aprobado && (
          <div><label>Monto aprobado</label><strong className="sol-green">S/ {fmt(s.monto_aprobado)}</strong></div>
        )}
        {credito && (
          <div><label>Crédito</label><strong className="sol-code-small">{credito.codigo_credito}</strong></div>
        )}
      </div>

      {(s.progreso || s.credito?.plan_cuotas?.length > 0) && (
        <ProgresoMini progreso={s.progreso} credito={s.credito} />
      )}

      {credito && (
        <div className={`sol-card__credito-pill ${isAprobado ? 'ok' : isRechazado ? 'rej' : 'neutral'}`}>
          <i className={`fa-solid ${isAprobado ? 'fa-circle-check' : isRechazado ? 'fa-circle-xmark' : 'fa-hourglass-half'}`}></i>
          <span>
            Crédito:{' '}
            {display.estado === 'PAGADO'
              ? 'Pagado'
              : credito.estado_proceso_label || credito.estado_proceso}
          </span>
          {credito.monto_aprobado && <span className="sol-card__credito-monto">S/ {fmt(credito.monto_aprobado)}</span>}
        </div>
      )}

      <div className="sol-card__footer">
        <div className="sol-card__footer-btns">
          <button className="sol-detail-btn" onClick={() => onDetail(s.id)} disabled={loadingDetail}>
            <i className="fa-solid fa-eye"></i> Ver detalle
          </button>
        </div>
      </div>
    </div>
  );
};

// ── SolicitudDetail ───────────────────────────────────────────────────────────
const SolicitudDetail = ({ s, onBack }) => {
  const credito       = s.credito;
  const tipoNombre    = s.tipo_credito?.nombre    || s.tipoCredito?.nombre    || '';
  const detNombre     = s.detalle_tipo_credito?.nombre || s.detalleTipoCredito?.nombre || '';
  const display       = getEstadoConCuotas(s);
  const isAprobado    = ['APROBADO','DESEMBOLSADO','PAGADO'].includes(display.estado);
  const isRechazado   = display.estado === 'RECHAZADO';
  const mostrarCuotas = ['APROBADO','DESEMBOLSADO','PAGADO'].includes(display.estado);

  // Filter requirements based on subtype
  const filteredRequisitos = filterRequisitos(s.requisitos, s.detalle_tipo_credito || s.detalleTipoCredito);

  // Calculate dynamic progress stats based on filtered requirements
  let reqTotal = s.progreso?.requisitos?.total ?? 0;
  let reqAprobados = s.progreso?.requisitos?.aprobados ?? 0;
  if (filteredRequisitos.length > 0) {
    reqTotal = filteredRequisitos.length;
    reqAprobados = filteredRequisitos.filter(r => r.estado_verificacion === 'APROBADO').length;
  }

  const dynamicProgreso = {
    ...s.progreso,
    requisitos: {
      total: reqTotal,
      aprobados: reqAprobados
    }
  };

  return (
    <div className="sol-detail">
      <button className="dash-back-btn" onClick={onBack}>
        <i className="fa-solid fa-arrow-left"></i> Volver
      </button>

      <div className="sol-detail__header">
        <div>
          <div className="sol-detail__code"><i className="fa-solid fa-file-contract"></i> {s.codigo_solicitud}</div>
          {(tipoNombre || detNombre) && (
            <div className="sol-detail__tipo">{tipoNombre}{tipoNombre && detNombre ? ' — ' : ''}{detNombre}</div>
          )}
        </div>
        <div className="sol-detail__estado">
          {estadoBadge(display.estado, display.label)}
          <span className="sol-detail__date"><i className="fa-regular fa-calendar"></i> {formatDate(s.fecha_solicitud)}</span>
          {s.fecha_aprobacion && (
            <span className="sol-detail__date"><i className="fa-solid fa-calendar-check"></i> Aprobado: {formatDate(s.fecha_aprobacion)}</span>
          )}
        </div>
      </div>

      <div className="dash-card sol-stepper-card">
        <StatusStepper estado={s.estado_solicitud} />
      </div>

      {credito && (isAprobado || isRechazado) && (
        <div className={`dash-card sol-credito-banner sol-credito-banner--${display.estado === 'PAGADO' ? 'ok' : isAprobado ? 'ok' : 'rejected'}`}>
          <div className="sol-credito-banner__info">
            <i className={`fa-solid ${display.estado === 'PAGADO' ? 'fa-circle-check' : isAprobado ? 'fa-circle-check' : 'fa-circle-xmark'}`}></i>
            <div>
              <strong>
                {display.estado === 'PAGADO'
                  ? '¡Tu crédito ha sido pagado por completo!'
                  : isAprobado
                  ? '¡Tu crédito fue aprobado!'
                  : 'Tu crédito fue rechazado'}
              </strong>
              {isAprobado && credito.monto_aprobado && (
                <p>Monto aprobado: <strong>S/ {fmt(credito.monto_aprobado)}</strong> · {credito.num_cuotas} cuotas · {credito.frecuencia_pago}</p>
              )}
              {!isAprobado && <p>Revisa los detalles y contacta con nosotros para más información.</p>}
            </div>
          </div>
        </div>
      )}

      {credito && !isAprobado && !isRechazado && (
        <div className="dash-card sol-credito-banner">
          <div className="sol-credito-banner__info">
            <i className="fa-solid fa-hourglass-half"></i>
            <div>
              <strong>Tu solicitud está en evaluación — {credito.codigo_credito}</strong>
              <p>Un asesor ya tiene tu expediente. Próximamente recibirás una respuesta.</p>
              {credito.progreso_requisitos != null && (
                <p style={{ marginTop: 6, fontSize: 12 }}>
                  Req: {credito.progreso_requisitos}% &nbsp;|&nbsp; Cond: {credito.progreso_condiciones}%
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="dash-card sol-detail__amounts">
        <div className="sol-detail__amount-grid">
          <div><label>Monto solicitado</label><strong>S/ {fmt(s.monto_solicitado)}</strong></div>
          <div><label>Plazo solicitado</label><strong>{s.plazo_solicitado} meses</strong></div>
          <div><label>Frecuencia de pago</label><strong>{s.frecuencia_pago}</strong></div>
          {s.monto_aprobado        && <div><label>Monto aprobado</label><strong className="sol-green">S/ {fmt(s.monto_aprobado)}</strong></div>}
          {s.plazo_aprobado        && <div><label>Plazo aprobado</label><strong>{s.plazo_aprobado} meses</strong></div>}
          {s.tasa_interes_aprobada && <div><label>Tasa aprobada</label><strong>{s.tasa_interes_aprobada}% TEM</strong></div>}
          {credito?.fecha_entrega  && <div><label>Fecha entrega</label><strong>{formatDate(credito.fecha_entrega)}</strong></div>}
          {credito?.fecha_desembolso && <div><label>Desembolsado</label><strong>{formatDate(credito.fecha_desembolso)}</strong></div>}
        </div>
      </div>

      {(s.progreso || s.credito?.plan_cuotas?.length > 0) && (
        <div className="dash-card">
          <h3 className="sol-detail__section-title">
            <i className="fa-solid fa-chart-line"></i>{' '}
            {s.credito?.plan_cuotas?.length > 0 ? 'Avance de pagos' : 'Progreso de evaluación'}
          </h3>
          <ProgresoMini progreso={dynamicProgreso} credito={s.credito} />
          {(!s.credito || !s.credito.plan_cuotas || s.credito.plan_cuotas.length === 0) && (
            <div className="sol-detail__req-stats" style={{ marginTop: 16 }}>
              <div className="sol-detail__req-stat">
                <span className="sol-detail__req-num">{reqTotal}</span>
                <span>Total req.</span>
              </div>
              <div className="sol-detail__req-stat sol-detail__req-stat--ok">
                <span className="sol-detail__req-num">{reqAprobados}</span>
                <span>Aprobados</span>
              </div>
              {s.progreso.condiciones?.total > 0 && (
                <>
                  <div className="sol-detail__req-stat">
                    <span className="sol-detail__req-num">{s.progreso.condiciones?.total ?? 0}</span>
                    <span>Total cond.</span>
                  </div>
                  <div className="sol-detail__req-stat sol-detail__req-stat--ok">
                    <span className="sol-detail__req-num">{s.progreso.condiciones?.aprobadas ?? 0}</span>
                    <span>Aprobadas</span>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      )}

      {filteredRequisitos.length > 0 && (
        <div className="dash-card">
          <h3 className="sol-detail__section-title">
            <i className="fa-solid fa-list-check"></i> Requisitos
            <span className="sol-detail__section-count">{filteredRequisitos.length}</span>
          </h3>
          <div className="sol-req-list">
            {filteredRequisitos.map((req) => <RequisitoRow key={req.requisito_id} req={req} />)}
          </div>
        </div>
      )}

      {s.condiciones?.length > 0 && (
        <div className="dash-card">
          <h3 className="sol-detail__section-title">
            <i className="fa-solid fa-file-contract"></i> Condiciones
            <span className="sol-detail__section-count">{s.condiciones.length}</span>
          </h3>
          <div className="sol-req-list">
            {s.condiciones.map((c, i) => (
              <div key={i} className="sol-req-row">
                <div className="sol-req-row__main">
                  <div className="sol-req-row__info">
                    <strong>{c.nombre_condicion || c.descripcion}</strong>
                    <div className="sol-req-row__badges">
                      <span className={`sol-cumple-badge${c.cumple ? ' sol-cumple-badge--si' : ' sol-cumple-badge--no'}`}>
                        {c.cumple ? <><i className="fa-solid fa-check"></i> Cumple</> : <><i className="fa-solid fa-xmark"></i> No cumple</>}
                      </span>
                      {c.valor_evaluado != null && (
                        <span className="sol-evidencia-count"><i className="fa-solid fa-hashtag"></i> S/ {fmt(c.valor_evaluado)}</span>
                      )}
                      {verificBadge(c.estado_verificacion || 'PENDIENTE')}
                    </div>
                    {c.observacion && <p className="sol-req-row__obs">{c.observacion}</p>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {mostrarCuotas && <PlanCuotas credito={credito} />}

      <Historial historial={s.historial} />

      {s.observaciones && (
        <div className="dash-card">
          <h3 className="sol-detail__section-title"><i className="fa-solid fa-comment-lines"></i> Observaciones</h3>
          <p className="sol-detail__obs-text">{s.observaciones}</p>
        </div>
      )}

      {s.motivo_rechazo && (
        <div className="dash-card" style={{ borderLeft: '4px solid #c0392b' }}>
          <h3 className="sol-detail__section-title" style={{ color: '#c0392b' }}>
            <i className="fa-solid fa-triangle-exclamation"></i> Motivo de rechazo
          </h3>
          <p className="sol-detail__obs-text">{s.motivo_rechazo}</p>
        </div>
      )}
    </div>
  );
};

// ── MisSolicitudes (main) ─────────────────────────────────────────────────────
const MisSolicitudes = () => {
  const [data, setData]                   = useState({ data: [], total: 0, page: 1 });
  const [selected, setSelected]           = useState(null);
  const [loading, setLoading]             = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError]                 = useState('');
  const [page, setPage]                   = useState(1);
  const [tab, setTab]                     = useState('activos'); // 'activos' | 'solicitudes'

  const fetchSolicitudes = useCallback(async (p = 1) => {
    setLoading(true); setError('');
    try {
      const res = await solicitudApi.getMisSolicitudes(p);
      setData(res);
    } catch (err) {
      if (err.status !== 401) setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSolicitudes(page); }, [fetchSolicitudes, page]);

  const openDetail = async (id) => {
    setDetailLoading(true);
    try {
      const res = await solicitudApi.getSolicitud(id);
      setSelected(res);
    } catch (err) {
      if (err.status !== 401) setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  if (loading && !data.data.length) return <div className="dash-loading"><div className="spinner"></div></div>;

  const lista     = data.data || [];
  const total     = data.total ?? lista.length;
  const totalPags = data.pages ?? Math.ceil(total / 10);

  // Separar por tabs: activos (DESEMBOLSADO o PAGADO) vs solicitudes (resto)
  const activos      = lista.filter(s => ['DESEMBOLSADO', 'PAGADO'].includes(getEstadoConCuotas(s).estado));
  const solicitudes  = lista.filter(s => !['DESEMBOLSADO', 'PAGADO'].includes(getEstadoConCuotas(s).estado));
  const listaFiltrada = tab === 'activos' ? activos : solicitudes;

  const enProceso = solicitudes.filter(s =>
    !['APROBADO','DESEMBOLSADO','RECHAZADO','CANCELADO'].includes(s.estado_solicitud)
  ).length;
  const aprobadas = solicitudes.filter(s =>
    ['APROBADO','DESEMBOLSADO'].includes(s.credito?.estado_proceso || s.estado_solicitud)
  ).length;

  if (selected) return (
    <div>
      <h2 className="dash-section-title">Mis Solicitudes</h2>
      <SolicitudDetail s={selected} onBack={() => setSelected(null)} />
    </div>
  );

  return (
    <div>
      <h2 className="dash-section-title">Mis Solicitudes</h2>
      <p className="dash-section-sub">Historial y estado de tus solicitudes de crédito</p>

      <div className="dash-quick-grid">
        <Link to="/dashboard/nueva-solicitud" className="dash-quick-card dash-quick-card--green">
          <div className="dash-quick-card__icon"><i className="fa-solid fa-file-pen"></i></div>
          <div className="dash-quick-card__label">Nueva Solicitud</div>
          <div className="dash-quick-card__desc">Simula y solicita tu crédito fácilmente</div>
        </Link>
        <Link to="/dashboard/perfil" className="dash-quick-card dash-quick-card--yellow">
          <div className="dash-quick-card__icon"><i className="fa-solid fa-user-gear"></i></div>
          <div className="dash-quick-card__label">Mi Perfil</div>
          <div className="dash-quick-card__desc">Actualiza tus datos personales</div>
        </Link>
      </div>

      <div className="dash-stat-strip">
        <div className="dash-stat-item">
          <div className="dash-stat-item__val">{total}</div>
          <div className="dash-stat-item__lbl">Total</div>
        </div>
        <div className="dash-stat-item">
          <div className="dash-stat-item__val" style={{ color: '#f39c12' }}>{enProceso}</div>
          <div className="dash-stat-item__lbl">En proceso</div>
        </div>
        <div className="dash-stat-item">
          <div className="dash-stat-item__val" style={{ color: '#27ae60' }}>{aprobadas}</div>
          <div className="dash-stat-item__lbl">Aprobadas</div>
        </div>
      </div>

      {error && <div className="dash-alert dash-alert--error">{error}</div>}

      {/* Tabs */}
      {lista.length > 0 && (
        <div className="sol-tabs">
          <button
            className={`sol-tab${tab === 'activos' ? ' sol-tab--active' : ''}`}
            onClick={() => setTab('activos')}
          >
            <i className="fa-solid fa-circle-check"></i> Créditos Activos
            <span className="sol-tab__count">{activos.length}</span>
          </button>
          <button
            className={`sol-tab${tab === 'solicitudes' ? ' sol-tab--active' : ''}`}
            onClick={() => setTab('solicitudes')}
          >
            <i className="fa-solid fa-file-lines"></i> Mis Solicitudes
            <span className="sol-tab__count">{solicitudes.length}</span>
          </button>
        </div>
      )}

      {error && <div className="dash-alert dash-alert--error">{error}</div>}

      {listaFiltrada.length === 0 ? (
        <div className="dash-card dash-empty">
          <i className="fa-solid fa-folder-open"></i>
          <p>{tab === 'activos' ? 'No tienes créditos activos.' : 'No tienes solicitudes pendientes.'}</p>
          {tab === 'solicitudes' && (
            <Link to="/dashboard/nueva-solicitud" className="btn-primary" style={{ marginTop: '1rem', display: 'inline-flex', gap: '0.5rem', alignItems: 'center' }}>
              <i className="fa-solid fa-plus"></i> Crear solicitud
            </Link>
          )}
        </div>
      ) : (
        <>
          <div className="sol-card-list">
            {listaFiltrada.map((s) => (
              <SolicitudCard key={s.id} s={s} onDetail={openDetail} loadingDetail={detailLoading} />
            ))}
          </div>
          {total > 10 && (
            <div className="sol-pagination">
              <button disabled={page === 1} onClick={() => setPage(p => p - 1)}>
                <i className="fa-solid fa-chevron-left"></i>
              </button>
              <span>Página {page} de {totalPags}</span>
              <button disabled={page >= totalPags} onClick={() => setPage(p => p + 1)}>
                <i className="fa-solid fa-chevron-right"></i>
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MisSolicitudes;
